import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { DiscountApplicationTypeNotEmpty } from "@intelligems/ig-types/dtos/offers";
import { ExperienceDto } from "@intelligems/ig-types/dtos/experiences";
import { ModKeys } from "@intelligems/ig-types/fe-only/personalizations";
import { hasPerGroupMods } from "@src/features/experiments/utils/hasPerGroupMods";

import {
  selectedActivatedModArray,
  setAllBatchVariationModsActivated,
  setBatchModsActivated,
} from "../../store/slices/experienceInfoState";

const evaluateTestTypes = (experience: ExperienceDto | undefined) => {
  const modsToActivate: ModKeys[] = [];
  if (experience) {
    if (experience.testTypes.hasTestPricing) {
      modsToActivate.push("price");
    }
    if (experience.testTypes.hasTestCheckoutBlocks) {
      modsToActivate.push("checkoutBlock");
    }
    if (experience.testTypes.hasTestPostPurchase) {
      modsToActivate.push("postPurchase");
    }

    if (experience.testTypes.hasTestShipping) {
      modsToActivate.push("shipping");
      modsToActivate.push("shippingProgressBar");
    }
    if (
      experience.testTypes.hasTestCampaign &&
      experience.variations[0].offer
    ) {
      modsToActivate.push(
        experience.variations[0].offer
          .discountApplicationType as DiscountApplicationTypeNotEmpty
      );
    }
    if (
      experience.testTypes.hasTestContentTheme ||
      experience.type === "content/advanced"
    ) {
      modsToActivate.push("theme");
    }
    if (
      experience.testTypes.hasTestContentTemplate ||
      experience.type === "content/advanced"
    ) {
      modsToActivate.push("template");
    }
    if (
      experience.testTypes.hasTestContentUrl ||
      experience.type === "content/advanced"
    ) {
      modsToActivate.push("splitUrl");
    }

    if (
      experience.testTypes.hasTestContentOnsite ||
      experience.category === "experiment"
    ) {
      modsToActivate.push("onsiteEdits");
    }
    if (
      experience.testTypes.hasTestOnsiteInjections ||
      experience.category === "experiment"
    ) {
      modsToActivate.push("onsiteInjections");
    }
    if (
      experience.testTypes.hasTestOnsiteUpsell ||
      experience.variations.some((variation) =>
        variation.components?.some(
          (component) => component.config.type === "onsiteUpsell"
        )
      )
    ) {
      modsToActivate.push("onsiteUpsell");
    }
  }
  return modsToActivate;
};

const evaluateVariationMods = (experience: ExperienceDto | undefined) => {
  const modsToActivate: { [variationId: string]: ModKeys[] } = {};
  if (experience?.variations) {
    experience.variations.forEach((variation) => {
      Object.assign(modsToActivate, { [variation.id]: { mods: [] } });
      if (variation?.offer?.discountApplicationType) {
        modsToActivate[variation.id]["mods"].push(
          variation?.offer?.discountApplicationType
        );
      }
      if (variation?.redirects?.length) {
        const foundThemeRedirects = variation.redirects.filter(
          (redirect) => redirect.isThemeTest
        );
        const foundTemplateRedirects = variation.redirects.filter(
          (redirect) => redirect.isTemplateTest
        );
        const foundUrlRedirects = variation.redirects.filter(
          (redirect) => !redirect.isThemeTest && !redirect.isTemplateTest
        );

        if (foundThemeRedirects.length > 0) {
          modsToActivate[variation.id]["mods"].push("theme");
        }
        if (foundTemplateRedirects.length > 0) {
          modsToActivate[variation.id]["mods"].push("template");
        }
        if (foundUrlRedirects.length > 0) {
          modsToActivate[variation.id]["mods"].push("splitUrl");
        }
      }
      if (variation.onsiteEdits.length) {
        modsToActivate[variation.id]["mods"].push("onsiteEdits");
      }

      if (variation.onsiteInjections?.variationId) {
        modsToActivate[variation.id]["mods"].push("onsiteInjections");
      }
      // Post-purchase offers come back in `checkoutBlocks` too, but they have
      // their own section.
      if (
        variation.checkoutBlocks?.some(
          (block) => block.blockType !== "postPurchaseUpsell"
        )
      ) {
        modsToActivate[variation.id]["mods"].push("checkoutBlock");
      }
      if (
        variation.checkoutBlocks?.some(
          (block) => block.blockType === "postPurchaseUpsell"
        )
      ) {
        modsToActivate[variation.id]["mods"].push("postPurchase");
      }
    });
  }
  return modsToActivate;
};

// Rebuilds the mod lists from a saved experience. A test whose groups each
// have their own mods gets one list per group, everything else gets one list.
export const activateModsFromSaved = (experience: ExperienceDto) =>
  experience.category === "experiment" && hasPerGroupMods(experience.type)
    ? setAllBatchVariationModsActivated(evaluateVariationMods(experience))
    : setBatchModsActivated({ mods: evaluateTestTypes(experience) });

export const useAndSetExperienceInfoStateFromState = (
  experience: ExperienceDto | undefined
) => {
  const dispatch = useDispatch();
  const activatedModArray = useSelector(selectedActivatedModArray);

  useEffect(() => {
    if (experience) {
      dispatch(activateModsFromSaved(experience));
    }
  }, [experience]);

  return activatedModArray;
};
